import { Component, OnInit, OnDestroy, HostListener, inject, ChangeDetectionStrategy } from '@angular/core';
import { IPortalNavigationNodes, LicenseType } from 'src/app/models/portal.model';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { NavigationHistoryService } from 'src/app/services/navigation-history/navigation-history.service';
import { LicenseService } from 'src/app/services/licenses/license.service';
import { ActionType } from 'src/app/services/licenses/licenses.model';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { WorkspaceLicensesService } from 'src/app/services/workspace-licenses/workspace-licenses.service';
import { UserAuthenticationService } from 'src/app/services/user-authentication/user-authentication.service';
import { MessageTypes } from 'src/app/models/enums.model';
import { WorkspaceLicense } from 'src/app/models/user-licenses.model';
import { getDaysDiferenceBetweenDates, isExpired } from 'src/app/models/util.model';
import { WorkspaceService } from 'src/app/services/workspace/workspace.service';
import { UtilService } from 'src/app/services/util/util.service';
import { UserWorkspaceStatus } from 'src/app/models/workspace.model';
import { expiredLicenseMessage, expiringLicenseMessage, intranetTrialLicensesMessage } from 'src/app/data/licenses-message.data';
import { IMessageAction } from 'src/app/element-components/message/message.component';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ExternalLinkDirective } from '../../directives/external-link/external-link.directive';
import { MessageComponent } from '../../element-components/message/message.component';
import { PortalService } from 'src/app/services/portal/portal.service';
import { UserEventsTrackingService } from 'src/app/services/user-events-tracking/user-events-tracking.service';

export interface LicenseNotification {
	product: string;
	type: MessageTypes;
	title?: string;
	message: string;
	dismiss: boolean;
	action?: IMessageAction;
}

@Component({
	selector: 'bt-sidebarnavigation',
	templateUrl: './sidebar-navigation.component.html',
	styleUrls: ['./sidebar-navigation.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgClass, ExternalLinkDirective, NgTemplateOutlet, MessageComponent]
})
export class SidebarNavigationComponent implements OnInit, OnDestroy {
	private router = inject(Router);
	private navigationHistoryService = inject(NavigationHistoryService);
	private settingsService = inject(SettingsService);
	private util = inject(UtilService);
	private workspaceService = inject(WorkspaceService);
	private workspaceLicensesService = inject(WorkspaceLicensesService);
	private authenticationService = inject(UserAuthenticationService);
	private licenseService = inject(LicenseService);
	private portalService = inject(PortalService);
	private userEventsTrackingService = inject(UserEventsTrackingService);

	public loading = true;
	public isAuthenticated = true;

	public sideBarNavigationOpened = false;
	public hoverSidebar = false;

	// Mobile responsive state
	public readonly MOBILE_BREAKPOINT = 768;
	public isMobile = false;
	public mobileMenuOpen = false;
	public mobileSubmenuOpen = false;
	public expandedMobileItem: IPortalNavigationNodes | null = null;

	public subMenuItem?: IPortalNavigationNodes;
	public sideNavigationItems: IPortalNavigationNodes[] = [];

	private subcriptions: Subscription[] = [];
	private isHomePage = false;
	public isNavigationPinned = false;

	public navigationWrapperWidth = 64;
	public hasValidLicenses = false;

	public licenseNotifications: LicenseNotification[] = [];
	public isActivatingTrial = false;

	public tokenSummary: { current: number | null; total: number | null; percent: number } = { current: null, total: null, percent: 0 };
	public hasAllLicenses = false;

	public onUpgradeClick(buttonLocation: 'mobile' | 'desktop'): void {
		this.userEventsTrackingService.addEvent_UpgradeClick(buttonLocation);
	}

	async ngOnInit(): Promise<void> {
		// Initialize mobile state
		this.checkMobileBreakpoint();

		const portalSettings = await this.portalService.loadPortalSettings();
		this.sideNavigationItems = portalSettings?.navigation.sidebarnavigation || [];
		
		if (this.sideNavigationItems.length > 0) {
			const path = location.pathname;
			this.updateMenuStyle(path);
			
			this.subcriptions.push(
				this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(
					async (event: NavigationEnd) => {
						// Update menu based on route
						this.updateMenuStyle(event.urlAfterRedirects);
					}
				)
			)
		}
		this.loading = false;

		this.isAuthenticated = this.authenticationService.isUserAuthenticated();
		if (this.isAuthenticated) {
			this.hasValidLicenses = await this.workspaceLicensesService.hasAnyValidLicenses();

			// Load license notifications for all products at initialization
			await this.updateLicenseMessage();

			// Check if user has all 3 main licenses (Automate, Intranet, Pulse365)
			await this.checkHasAllLicenses();

			// expose derived summary observable from store for template usage (async pipe)
			this.subcriptions.push(
				this.licenseService.getTokensSummaryObservable().subscribe(value => {
					if (value) {
						this.tokenSummary = value;
					}
				})
			)
		}

		// Subscribe to mobile menu state from service
		this.subcriptions.push(
			this.settingsService.mobileMenuOpen$.subscribe((isOpen) => {
				if (isOpen && !this.mobileMenuOpen) {
					this.openMobileMenu();
				} else if (!isOpen && this.mobileMenuOpen) {
					this.closeMobileMenu();
				}
			})
		)
	}

	public ngOnDestroy(): void {
		this.subcriptions.forEach(s => s.unsubscribe());
		this.closeMobileMenu();
	}

	//#region Mobile Navigation Methods

	/**
	 * Listen for window resize events to update mobile state
	 */
	@HostListener('window:resize')
	onWindowResize(): void {
		this.checkMobileBreakpoint();
	}

	/**
	 * Listen for escape key to close mobile menu
	 */
	@HostListener('document:keydown.escape')
	onEscapeKey(): void {
		if (this.mobileMenuOpen) {
			this.closeMobileMenu();
		}
	}

	/**
	 * Check if viewport is mobile size and update state accordingly
	 */
	private checkMobileBreakpoint(): void {
		const wasMobile = this.isMobile;
		this.isMobile = window.innerWidth <= this.MOBILE_BREAKPOINT;

		// Close mobile menu when transitioning to desktop
		if (wasMobile && !this.isMobile && this.mobileMenuOpen) {
			this.closeMobileMenu();
		}

		this.setContainerWidth();
	}

	/**
	 * Toggle mobile menu open/closed
	 */
	public toggleMobileMenu(): void {
		if (this.mobileMenuOpen) {
			this.closeMobileMenu();
		} else {
			this.openMobileMenu();
		}
	}

	/**
	 * Open the mobile menu drawer
	 */
	public openMobileMenu(): void {
		this.mobileMenuOpen = true;
		this.mobileSubmenuOpen = false;
		this.expandedMobileItem = null;
		document.body.classList.add('mobile-nav-open');
	}

	/**
	 * Close the mobile menu drawer
	 */
	public closeMobileMenu(): void {
		this.mobileMenuOpen = false;
		this.mobileSubmenuOpen = false;
		this.expandedMobileItem = null;
		document.body.classList.remove('mobile-nav-open');
		// Notify service that menu is closed
		if (this.settingsService.isMobileMenuOpen()) {
			this.settingsService.closeMobileMenu();
		}
	}

	/**
	 * Handle mobile navigation item click
	 * If item has children, expand submenu; otherwise navigate and close menu
	 */
	public onMobileNavItemClick(item: IPortalNavigationNodes): void {
		if (item.isDisabled) return;

		const childs = item.sections?.flatMap(s => s.childs) ?? [];

		if (childs.length > 0) {
			// Has children - toggle submenu
			if (this.expandedMobileItem?.title === item.title) {
				this.expandedMobileItem = null;
				this.mobileSubmenuOpen = false;
			} else {
				this.expandedMobileItem = item;
				this.mobileSubmenuOpen = true;
			}
		} else {
			// No children - navigate directly
			this.navigateMenuItem(item);
			this.closeMobileMenu();
		}
	}

	/**
	 * Handle mobile submenu item click - navigate and close menu
	 */
	public onMobileSubNavItemClick(item: IPortalNavigationNodes): void {
		if (item.isDisabled) return;
		this.navigateSubMenuItem(item, true);
		this.closeMobileMenu();
	}

	/**
	 * Go back from mobile submenu to main menu
	 */
	public mobileGoBack(): void {
		this.expandedMobileItem = null;
		this.mobileSubmenuOpen = false;
	}

	//#endregion
	private async updateLicenseMessage(): Promise<void> {
		if (this.authenticationService.isUserAuthenticated()) {
			this.licenseNotifications = [];

			// Check all 3 products for license notifications
			const bundles = (await this.workspaceLicensesService.getWorkspaceLicenses())
			  .filter(license => license.sku?.startsWith('WINT-') || license.sku?.startsWith('WORK-INTEG'));
			if (bundles.length > 0) {
			  for (const bundle of bundles) {
			    await this.addNotificationForLicense(bundle);
			  }
			  return;
			}

			const products: LicenseType[] = ['pulse', 'automate', 'intranet-webparts'];

			for (const product of products) {
				const license = await this.workspaceLicensesService.getLicenseBasedOnLicenseType(product);
				if (license) {
					await this.addNotificationForLicense(license);
				}
			}
		}
	}

	/**
	 * Activate Intranet trial using tokens.
	 */
	public async activateIntranetTrial(): Promise<void> {
		const tokenPackage = await this.workspaceLicensesService.getLicenseBasedOnLicenseType('tokens');
		const tokenBalance = tokenPackage?.remainingAmount ?? 0;

		// Get intranet activation license to check remaining executions
		const allLicenses = await this.workspaceLicensesService.getWorkspaceLicenses();
		const intranetActivationLicense = allLicenses?.find(x => x.sku.startsWith('TOKN-INTACT-'));
		const executionsRemaining = intranetActivationLicense?.remainingAmount ?? 0;

		if (executionsRemaining === 0) {
			// No more executions available
			this.router.navigate([{ outlets: { modal: ['intranet-license-required'] } }], { skipLocationChange: true });
		}

		const cost = await this.licenseService.getActionCostAsync(ActionType.IntranetTrialActivation, executionsRemaining) ?? 0;
		if (tokenBalance < cost) {
			// Not enough tokens - show tokens exhausted modal
			this.router.navigate(
				[{ outlets: { modal: ['tokens-exhausted'] } }],
				{
					queryParams: {
						productType: 'intranet',
						reason: 'no-tokens',
						balance: tokenBalance,
						cost: cost
					},
					queryParamsHandling: 'merge',
					skipLocationChange: true
				}
			);
			return;
		}

		if (this.isActivatingTrial) return;

		this.isActivatingTrial = true;
		const success = await this.licenseService.activateFeature(ActionType.IntranetTrialActivation);

		if (success) {
			// Refresh the license message after activation
			await this.updateLicenseMessage();
		}

		this.isActivatingTrial = false;
	}

	/**
	 * Check if user has all 3 main licenses (Automate, Intranet, Pulse365)
	 * If they have all 3 valid licenses, hide the credits/tokens summary
	 */
	private async checkHasAllLicenses(): Promise<void> {
		const [automateLicense, intranetLicense, pulseLicense] = await Promise.all([
			this.workspaceLicensesService.validateLicenseByType('automate'),
			this.workspaceLicensesService.validateLicenseByType('intranet-webparts'),
			this.workspaceLicensesService.validateLicenseByType('pulse')
		]);

		this.hasAllLicenses = automateLicense !== 'noLicense' && intranetLicense !== 'noLicense' && pulseLicense !== 'noLicense';
	}

	public async addNotificationForLicense(license: WorkspaceLicense) {
		const licenseIsValid = !isExpired(license.validThru) && this.workspaceService.getCurrentUserWorkspace().status.toLowerCase() === UserWorkspaceStatus.Enable;
		const productName = license.sku.startsWith("WINT-") || license.sku.startsWith("WORK-INTEG") ? "integrity" : license.sku.startsWith("A365-") ? "automate" : (license.sku.startsWith("P365-") ? "pulse" : "intranet");
		const expiringDate = this.util.getDateString(new Date(license.validThru));
		const isTrial = license.constraints.indexOf("subscription_trial") !== -1;
		const daysToExpire = getDaysDiferenceBetweenDates(new Date(license.validThru));

		let notification: LicenseNotification | null = null;

		if (!licenseIsValid) {
			if (isTrial && productName === 'intranet') {
				const allLicenses = await this.workspaceLicensesService.getWorkspaceLicenses();
				const isFirst = allLicenses.some(l => l.sku?.startsWith('TOKN-INTACT-'));
				if (isFirst) {
					notification = {
						product: productName,
						type: 'Danger',
						dismiss: false,
						message: intranetTrialLicensesMessage.expiredTrialFirst.description.replace('##date##', expiringDate),
						action: {
							position: 'bottom',
							label: intranetTrialLicensesMessage.expiredTrialFirst.action.label,
							action: () => this.activateIntranetTrial()
						}
					};
				} else {
					notification = {
						product: productName,
						type: 'Danger',
						dismiss: false,
						message: intranetTrialLicensesMessage.expiredTrialSecond.description.replace('##date##', expiringDate),
						action: {
							position: 'bottom',
							label: intranetTrialLicensesMessage.expiredTrialSecond.action.label,
							action: () => this.activateIntranetTrial()
						}
					};
				}
			} else if (!isTrial || productName === 'integrity') {
				notification = {
					product: productName,
					type: 'Danger',
					dismiss: false,
					message: expiredLicenseMessage[productName].description.replace('##date##', expiringDate)
				};
			}
		} else if (daysToExpire < 7) {
			if (isTrial && productName === 'intranet') {
				const allLicenses = await this.workspaceLicensesService.getWorkspaceLicenses();
				const isFirst = allLicenses.some(l => l.sku?.startsWith('TOKN-INTACT-'));
				if (isFirst) {
					notification = {
						product: productName,
						type: 'Warning',
						dismiss: true,
						message: intranetTrialLicensesMessage.expiringTrialFirst.description.replace('##date##', expiringDate)
					};
				} else {
					notification = {
						product: productName,
						type: 'Warning',
						dismiss: true,
						message: intranetTrialLicensesMessage.expiringTrialSecond.description.replace('##date##', expiringDate)
					};
				}
			} else if (!isTrial || productName === 'integrity') {
				notification = {
					product: productName,
					type: 'Warning',
					dismiss: true,
					message: expiringLicenseMessage[productName].description.replace('##date##', expiringDate)
				};
			}
		}

		if (notification) {
			this.licenseNotifications.push(notification);
		}
	}


	public showSidebar() {
		this.navigationWrapperWidth = 64;
		this.setContainerWidth();
	}

	public hideSidebar() {
		this.navigationWrapperWidth = 0;
		this.setContainerWidth();
	}

	// Open and close the sidebar
	public openCloseSidebar() {
		this.sideBarNavigationOpened = !this.sideBarNavigationOpened;
		this.setContainerWidth();
	}

	// Prevent navigation with changes to save
	public navigateMenuItem(item: IPortalNavigationNodes, navigate = true) {
		this.navigate(item, navigate);
	}

	// Navigate to children
	public navigateSubMenuItem(item: IPortalNavigationNodes, navigate = true) {
		this.navigate(item, navigate);
	}

	public pinNavigation() {
		this.isNavigationPinned = !this.isNavigationPinned;
		this.setContainerWidth();
	}
	/**
	 * Handles navigation logic for sidebar navigation items.
	 *
	 * - If the item is not disabled and has child nodes, it sets the submenu item,
	 *   clears the forward navigation history, and navigates to the first child's URL.
	 * - If the item has no child nodes, it sets the submenu item to the parent (if provided),
	 *   clears the forward navigation history, and navigates to the item's URL.
	 *
	 * @param item The navigation node to navigate to.
	 * @param navigate Optional. Whether to perform navigation. Defaults to true.
	 */
	private navigate(item: IPortalNavigationNodes, navigate = true) {
		if (!item.isDisabled) {
			const childs = item.sections?.flatMap(s => s.childs) ?? [];
			// If the item has child nodes, toggle its collapsed state
			if (childs && childs.length > 0) {
				this.navigationHistoryService.clearFowardsHistory();
				const menuUrl = childs.find(c => c.isDefault)?.url ?? childs[0].url ?? "";
				this.router.navigateByUrl(menuUrl); // Navigate to the first child if available
			} else {
				if (item.url && navigate) {
					this.navigationHistoryService.clearFowardsHistory();
					this.router.navigateByUrl(item.url);
				}
			}
		}
	}

	// Update menu styles based on route
	private async updateMenuStyle(path: string) {


		// Here we do a foreach to verify and update all values
		await Promise.all(this.sideNavigationItems.map(async navItem => {
			// If the url exists (first level) - Check the starts with
			if (navItem.url) {
				navItem.isActive = path.startsWith(navItem.url)
			}
			else {
				navItem.isActive = false
			}

			this.isHomePage = path === '/home' ? true : false;

			// If the naviation has childs validate the childs values
			if (navItem.sections && navItem.sections.length > 0) {
				let parentIsActive = false
				for (const section of navItem.sections) {
					for (const childMenuItem of section.childs) {

						//################## This are exceptions to the tabs (we need to do this here) ##################
						if (path.includes('intranet/mythemes') || path.includes('intranet/sharedthemes')) { path = '/intranet/themes' }
						if (path.includes('automate/mytemplates') || path.includes('automate/orgtemplates') || path.includes('automate/sharedtemplates') || path.includes('automate/settings-templates') || path.includes('automate/sharepointtemplates')) { path = '/automate/templates' }
						if (path.includes('automate/approvals/list') || path.includes('automate/approvals/requests')) { path = '/automate/approvals' }
						if (path.includes('automate/automations/list') || path.includes('automate/automations/runs')) { path = '/automate/automations' }
						//################## This are exceptions to the tabs (we need to do this here) ##################

						if (childMenuItem.url && path.startsWith(childMenuItem.url)) {
							parentIsActive = true
							navItem.isCollapsed = true;
							childMenuItem.isActive = true
						} else {
							childMenuItem.isActive = false
						}
					}
				}

				navItem.isActive = parentIsActive;
			}
		}));


		const parentLevelActive = this.sideNavigationItems.find(x => x.isActive);
		this.subMenuItem = parentLevelActive?.sections?.flatMap(s => s.childs)?.length === 0 ? undefined : parentLevelActive;
		this.sideBarNavigationOpened = !this.subMenuItem;
		this.setContainerWidth();

	}

	private setContainerWidth() {
		const mainWrapper = document.getElementById('main-wrapper');
		if (mainWrapper) {

			// On mobile, main wrapper takes full width
			if (this.isMobile) {
				mainWrapper.style.width = '100%';
				mainWrapper.style.paddingLeft = '0';
				return;
			}

			if (this.navigationWrapperWidth === 0) {
				mainWrapper.style.width = '100%';
			}
			else {

				this.navigationWrapperWidth = 0;

				if (!this.sideBarNavigationOpened && !this.isNavigationPinned) {
					this.navigationWrapperWidth = 64;
				}
				else if (this.sideBarNavigationOpened || this.isNavigationPinned) {
					this.navigationWrapperWidth = 232;
				}


				// If there is a submenu we add the width of the submenu
				if (this.subMenuItem) {
					this.navigationWrapperWidth += 232
				}

				mainWrapper.style.width = 'calc(100% - ' + this.navigationWrapperWidth + 'px)';
			}
			if (this.isHomePage) {
				mainWrapper.style.maxWidth = '1920px';
				mainWrapper.style.margin = '0 auto';
			}
			else {
				mainWrapper.style.maxWidth = 'unset';
				mainWrapper.style.margin = 'unset';
			}
		}
	}

	public goToSubscriptionsPage() {
		this.router.navigate(['/settings/workspace/subscription']);
	}
}
